import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { GraduationCap, Hash, LogIn, Newspaper, UserPlus } from 'lucide-react';
import { publicService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Brand, Button, EmptyState, Loader, PageHeader } from '../components/ui';
import { ArticleCover, TagPill, useForcedDarkTheme } from '../components/articles/ArticleBits';
import { shortDate } from '../utils/articles';
import '../components/articles/articles.css';
import './PublicArticles.css';

const PAGE = 12;

/** Artigos públicos (/a) para quem não tem conta, sempre no tema escuro. Logado, vai para a lista completa. */
function PublicArticles() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [busy, setBusy] = useState(false);
  const [tag, setTag] = useState('');
  useForcedDarkTheme();

  useEffect(() => {
    if (loading || user) return;
    setBusy(true);
    publicService.articles(PAGE, page)
      .then(r => {
        setItems(prev => [...(page === 0 ? [] : prev || []), ...r.items]);
        setTotal(r.total);
      })
      .catch(() => setItems(prev => prev || []))
      .finally(() => setBusy(false));
  }, [page, loading, user]);

  // A API pública traz um tema por artigo: os chips saem dos artigos já carregados
  const tags = useMemo(() => {
    const seen = new Map();
    (items || []).forEach(a => { if (a.tag && !seen.has(a.tag.slug)) seen.set(a.tag.slug, a.tag); });
    return [...seen.values()];
  }, [items]);
  const shown = useMemo(() => (items || []).filter(a => !tag || a.tag?.slug === tag), [items, tag]);
  const count = tag ? shown.length : total;

  if (loading) return <Loader label="Carregando..." />;
  if (user) return <Navigate to="/artigos" replace />;

  const login = () => navigate('/login', { state: { from: '/artigos' } });
  const open = (a) => navigate(`/a/${a.slug}`);

  return (
    <div className="ss-shell pa-shell">
      <header className="ss-topbar">
        <div className="ss-topbar__inner">
          <div className="ss-topbar__left">
            <button type="button" className="ss-topbar__brand" onClick={() => navigate('/')} aria-label="Início"><Brand size="sm" /></button>
            <nav className="ss-globalnav" aria-label="Seções">
              <Link to="/a" className="ss-globalnav__link is-active" aria-current="page">
                <Newspaper size={16} strokeWidth={2.2} aria-hidden="true" />
                <span>Artigos</span>
              </Link>
            </nav>
          </div>
          <span className="pa-actions">
            <Button variant="ghost" icon={LogIn} onClick={login}>Entrar</Button>
            <Button variant="accent" icon={UserPlus} onClick={() => navigate('/solicitar-acesso')}>Solicitar acesso</Button>
          </span>
        </div>
      </header>
      <main className="ss-main">
        <div className="ss-page ss-page--wide pa-page">
          <PageHeader
            icon={Newspaper}
            eyebrow="Leitura aberta"
            title="Artigos públicos"
            subtitle="Escritos por quem estuda e por quem ensina. Para curtir, comentar e salvar, é só entrar."
          />
          {!items && <Loader label="Carregando artigos..." />}
          {items?.length === 0 && (
            <EmptyState file="artigos.xml" title="Ainda não há artigos públicos" description="Os artigos abertos aparecem aqui assim que a turma publicar." />
          )}
          {items?.length > 0 && (
            <>
              <div className="pa-filters">
                <div className="pa-filters__tags">
                  <TagPill tag="Todos" active={!tag} onClick={() => setTag('')} />
                  {tags.map(t => (
                    <TagPill key={t.slug} tag={t.name} active={tag === t.slug} onClick={() => setTag(tag === t.slug ? '' : t.slug)} />
                  ))}
                </div>
                <span className="pa-filters__count">{count} {count === 1 ? 'artigo' : 'artigos'}</span>
              </div>

              <div className="pa-grid">
                {shown.map((a, i) => (
                  <PublicCard key={a.slug} article={a} lead={i === 0 && !tag} onOpen={open} />
                ))}
              </div>
              {!tag && items.length < total && (
                <div className="pa-more">
                  <Button variant="outline" onClick={() => setPage(p => p + 1)} disabled={busy}>Carregar mais</Button>
                </div>
              )}
            </>
          )}

          <div className="pa-invite">
            <span className="pa-invite__icon"><GraduationCap size={18} aria-hidden="true" /></span>
            <span className="pa-invite__text">
              <strong>Estuda numa das turmas?</strong>
              <span>Entre para curtir, salvar e ler os artigos só para alunos.</span>
            </span>
            <span className="pa-invite__actions">
              <Button variant="outline" icon={LogIn} onClick={login}>Entrar</Button>
              <Button variant="accent" onClick={() => navigate('/solicitar-acesso')}>Solicitar acesso</Button>
            </span>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Card da grade pública; o mais recente ocupa 2 colunas com a capa ao lado. */
function PublicCard({ article: a, lead, onOpen }) {
  return (
    <div
      className={`pa-card ${lead ? 'pa-card--lead' : ''}`}
      role="link"
      tabIndex={0}
      onClick={() => onOpen(a)}
      onKeyDown={(e) => e.key === 'Enter' && onOpen(a)}
    >
      <ArticleCover article={a} className="pa-card__cover" />
      <div className="pa-card__body">
        {lead && <span className="pa-card__badge">Mais recente</span>}
        {a.tag && <span className="pa-card__tag"><Hash size={10} strokeWidth={2.8} aria-hidden="true" />{a.tag.name}</span>}
        <span className="pa-card__title">{a.title}</span>
        {a.subtitle && <span className="pa-card__excerpt">{a.subtitle}</span>}
        <span className="pa-card__by">
          <strong>{a.author}</strong> · {shortDate(a.publishedAt)} · {a.readingMinutes} min
        </span>
      </div>
    </div>
  );
}

export default PublicArticles;
